import React, { useCallback, useMemo, useState } from "react";

import { GanttProps } from "components/ComponentType";
import { IProductTemplate, ITableDataEntry } from "components/dashboard/product-template-table/utils/interfaces";
import { Config, Data, Layout, PlotData, PlotRelayoutEvent } from "plotly.js";
import Plot from "react-plotly.js";
import { T } from "services/lang/LanguageService";
import { formatDate, formatDateDuration } from "utils/time-utils";
import copyChartToClipBoard from "../../chartUtils";
import { clipboardIcon } from "../../icons";

type GanttChartProps = GanttProps & {
  templateData: ITableDataEntry[],
  configData: IProductTemplate | undefined,
}
const OFFSET_HIGH = 0.4;
const MAX_VISIBLE_LABELS = 40;

const GanttChart: React.FC<GanttChartProps> = ({templateData, configData, ...props}) => {
  const getAdditionalInfo = useCallback((tableData: ITableDataEntry) => {
    return Object.values(tableData.entries).reduce((arr, val) => {
      if (val.type !== "INSTANT") {
        return `${arr}<br>${configData?.data?.attributes.find(a => a.referenceName === val.referenceName)?.displayName}: ${val.value}`;
      }
      return arr;
    }, "");
  }, [configData?.data?.attributes]);

  const sortedTemplateData = templateData.sort((a, b) => a.keyValue.localeCompare(b.keyValue));

  const ranges: Partial<PlotData>[] = useMemo(() => {
    const legends = new Set<string>();
    return sortedTemplateData.reduce((array, acc, index) => {
      props.ganttRanges.forEach(gr => {
        const startDate = acc.entries[gr.startDateColumn!]?.value?.toString();
        const endDate =
          acc.entries[gr.endDateColumn!]?.value?.toString() ?? new Date().toISOString();
        if (startDate) {
          const hoverInfo =
            `${gr.title}<br>` +
            `${T("layout.start")}: ${formatDate(startDate)}<br>` +
            `${T("layout.end")}: ${formatDate(endDate)}<br>` +
            `${T("word.duration")}: ${formatDateDuration(startDate, endDate)}` +
            `${configData && getAdditionalInfo(acc)}`;

          const trace: Partial<PlotData> = {
            mode: "lines",
            fill: "toself",
            fillcolor: gr.color,
            opacity: 1,
            name: gr.title ?? "",
            x: [startDate, endDate, endDate, startDate, startDate],
            y: [
              index - OFFSET_HIGH,
              index - OFFSET_HIGH,
              index + OFFSET_HIGH,
              index + OFFSET_HIGH,
              index - OFFSET_HIGH,
            ],
            line: { color: gr.color },
            showlegend: !legends.has(gr.title ?? ""),
            legendgroup: gr.title ?? "",
            text: hoverInfo,
            hoverinfo: "text",
            hoverlabel: {
              bgcolor: gr.color,
            },
          };
          legends.add(gr.title ?? "");
          const mark: Partial<PlotData> = {
            name: gr.title ?? "",
            x: [endDate, endDate],
            y: [index, index],
            marker: { color: gr.color, size: 10 },
            opacity: 0,
            showlegend: false,
            text: hoverInfo,
            hoverinfo: "text",
          };
          array.push(trace);
          array.push(mark);
        }
      });
      return array;
    }, [] as Partial<PlotData>[]);
  }, [configData, getAdditionalInfo, props.ganttRanges, sortedTemplateData]);

  const timestamps: Partial<PlotData>[] = useMemo(
    () => {
      const legends = new Set<string>();
      return sortedTemplateData.reduce((array, acc, index) => {
        props.ganttTimestamps.forEach(gt => {
          const date = acc.entries[gt.dateColumn!]?.value?.toString();
          const trace: Partial<PlotData> = {
            name: gt.title ?? "",
            x: [date, date],
            y: [index, index],
            marker: { color: gt.color, symbol: gt.icon ?? "circle", size: 10 },
            line: { color: "transparent" },
            showlegend: !legends.has(gt.title ?? ""),
            legendgroup: gt.title ?? "",
            text:
              `${gt.title}<br>` +
              `${T("word.date")}: ${formatDate(date)}` +
              `${configData && getAdditionalInfo(acc)}`,
            hoverinfo: "text",
          };
          legends.add(gt.title ?? "");
          array.push(trace);
        });
        return array;
      
    }, [] as Partial<PlotData>[])},
    [configData, getAdditionalInfo, props.ganttTimestamps, sortedTemplateData]
  );

  const yLabels = useMemo(
    () =>
      sortedTemplateData.map(
        td =>
          td.entries[props.columnOverrideYLabels]?.value?.toString() ??
          td.stringRepresentation ??
          "-"
      ),
    [props.columnOverrideYLabels, sortedTemplateData]
  );

  const data: Data[] = [...ranges, ...timestamps];
  const layout: Partial<Layout> = useMemo(
    () => ({
      xaxis: {
        type: "date",
        showgrid: true,
        zeroline: false,
      },
      yaxis: {
        range: [-1, sortedTemplateData.length],
        showgrid: true,
        ticktext: yLabels,
        showticklabels: yLabels.length <= MAX_VISIBLE_LABELS,
        tickvals: sortedTemplateData.map((_td, i) => i),
        zeroline: false,
        autorange: false,
      },
      height: props.height ?? 500,
      hovermode: "closest",
      showlegend: props.showLegends ?? true,
      legend: { orientation: "h" },
      margin: {
        l: Math.max(...yLabels.map(y => y.length)) * 7,
        r: 0,
        b: 20,
        t: 20,
      },
    }),
    [props.height, props.showLegends, sortedTemplateData, yLabels]
  );
  const [currentLayout, setCurrentLayout] = useState(layout);

  const handleZoom = useCallback((eventData: Readonly<PlotRelayoutEvent>) => {
    const yRange0 = eventData["yaxis.range[0]"] ?? eventData["yaxis.range"]?.[0] as number;
    const yRange1 = eventData["yaxis.range[1]"] ?? eventData["yaxis.range"]?.[1] as number;
    const yAutoRange = eventData["yaxis.autorange"] ?? false;
    if (!yAutoRange && !yRange0) return;
    const visibleLabels = Math.abs(yRange1 - yRange0);
    const showLabels = visibleLabels <= MAX_VISIBLE_LABELS;

    const newLayout = {
      ...currentLayout,
      yaxis: {
        ...currentLayout.yaxis,
        showticklabels: yAutoRange ? yLabels.length <= MAX_VISIBLE_LABELS : showLabels,
      },
    };

    setCurrentLayout(newLayout);
  }, [currentLayout, yLabels]);

  const chartConfig: Partial<Config> = {
    displaylogo: false,
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    modeBarButtonsToAdd: [
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
    ],
  };

  return (
    <Plot
      data={data}
      layout={currentLayout}
      config={chartConfig}
      className="w-full"
      useResizeHandler
      onRelayout={handleZoom}
    />
  );
};

export default GanttChart;
